import { ApiRequestError, apiRequest } from '../api';
import { t } from '../i18n';
import { $, h, rebind } from '../ui/dom';

function tzOptions(): HTMLOptionElement[] {
  const list: HTMLOptionElement[] = [];
  for (let off = -12; off <= 14; off++) {
    const label = off === 0 ? 'UTC' : `UTC${off > 0 ? '+' : '−'}${Math.abs(off)}`;
    list.push(h('option', { attrs: { value: label.replace('−', '-') } }));
  }
  return list;
}

function guessTimezone(): string {
  const off = -new Date().getTimezoneOffset() / 60;
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const base = off === 0 ? 'UTC' : `UTC${off > 0 ? '+' : '-'}${Math.abs(off)}`;
  return zone ? `${base} (${zone})` : base;
}

function isUrl(v: string): boolean {
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function mountJoin(): void {
  const form = $('join-form') as HTMLFormElement | null;
  const successEl = $('join-success');
  const errorBox = $('join-error');
  if (!form || !successEl || !errorBox) return;
  const success: HTMLElement = successEl;

  const datalist = $('tz-list');
  if (datalist && !datalist.childElementCount) datalist.append(...tzOptions());
  const tz = form.elements.namedItem('timezone') as HTMLInputElement | null;
  if (tz && !tz.value) tz.value = guessTimezone();

  const showError = (msg: string | null): void => {
    errorBox.textContent = msg ?? '';
    errorBox.classList.toggle('hidden', !msg);
  };

  const fresh = rebind<HTMLFormElement>('join-form', 'submit', (e) => {
    e.preventDefault();
    void submit();
  });
  if (!fresh) return;

  rebind('join-again', 'click', () => {
    fresh.reset();
    const tzField = fresh.elements.namedItem('timezone') as HTMLInputElement | null;
    if (tzField) tzField.value = guessTimezone();
    success.classList.add('hidden');
    fresh.classList.remove('hidden');
    showError(null);
  });

  async function submit(): Promise<void> {
    if (!fresh) return;
    showError(null);
    const fd = new FormData(fresh);
    const get = (k: string): string => String(fd.get(k) ?? '').trim();
    const payload = {
      nickname: get('nickname'),
      profile_url: get('profile_url'),
      mmr: get('mmr'),
      timezone: get('timezone'),
      contact: get('contact'),
      comment: get('comment'),
      website: get('website'),
    };

    // Client-side checks mirror the server; the server stays the source of truth.
    fresh.querySelectorAll('.field').forEach((f) => f.classList.remove('invalid'));
    const invalid: string[] = [];
    for (const k of ['nickname', 'profile_url', 'mmr', 'timezone', 'contact'] as const) {
      if (!payload[k]) invalid.push(k);
    }
    if (payload.profile_url && !isUrl(payload.profile_url)) invalid.push('profile_url');
    const mmr = Number(payload.mmr);
    if (payload.mmr && (!Number.isInteger(mmr) || mmr < 0 || mmr > 10000)) invalid.push('mmr');
    if (invalid.length) {
      for (const k of invalid) fresh.querySelector(`[name="${k}"]`)?.closest('.field')?.classList.add('invalid');
      const first = fresh.querySelector<HTMLElement>(`[name="${invalid[0]}"]`);
      first?.focus();
      const label = first?.closest('.field')?.querySelector('.field-label span')?.textContent ?? '';
      showError(t('join.invalid', { error: `${label} — ${t('join.required')}` }));
      return;
    }

    const btn = $('join-submit') as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = true;
      btn.textContent = t('join.sending');
    }
    try {
      await apiRequest('/api/applications', { method: 'POST', body: { ...payload, mmr } });
      fresh.classList.add('hidden');
      success.classList.remove('hidden');
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 429) showError(t('join.rateLimited'));
      else if (err instanceof ApiRequestError && err.status === 400) showError(t('join.invalid', { error: err.message }));
      else showError(t('join.failed'));
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = t('join.submit');
      }
    }
  }
}
